/**
 * BreadcrumbControl
 * Renders a breadcrumb navigation trail.
 * Config: items[] = { label: string; href?: string }
 * Value: active step index (number) when controlled.
 */

import React, { memo } from 'react';
import type { FormControl } from '@atlas-forms/types-js';
import { ChevronRightIcon } from '@atlas-forms/ui-components-react';

export interface BreadcrumbItem {
  label: string;
  href?: string;
}

export interface BreadcrumbControlProps {
  control: FormControl;
  value?: number;
  onChange?: (v: number) => void;
  mode?: 'edit' | 'view';
}

export const BreadcrumbControl = memo(function BreadcrumbControl({ control, value, onChange, mode = 'edit' }: BreadcrumbControlProps) {
  const cfg = (control as any).config ?? {};
  const items: BreadcrumbItem[] = cfg.items ?? [];
  const activeIndex = typeof value === 'number' ? value : items.length - 1;

  const navStyle: React.CSSProperties = {
    display: 'flex',
    alignItems: 'center',
    flexWrap: 'wrap',
    gap: 4,
    padding: '8px 0',
    fontSize: 14,
  };

  const itemStyle = (i: number): React.CSSProperties => ({
    color: i === activeIndex ? '#f8fafc' : '#94a3b8',
    fontWeight: i === activeIndex ? 600 : 400,
    cursor: mode !== 'view' && onChange && i < activeIndex ? 'pointer' : 'default',
    textDecoration: 'none',
    transition: 'color 0.15s',
  });

  const separatorStyle: React.CSSProperties = {
    color: '#475569',
    display: 'flex',
    alignItems: 'center',
  };

  if (items.length === 0) {
    return (
      <nav aria-label="breadcrumb" data-control-id={control.id} data-control-type="breadcrumb">
        <span style={{ fontSize: 13, opacity: 0.5, color: '#94a3b8' }}>No breadcrumb items configured</span>
      </nav>
    );
  }

  return (
    <nav aria-label="breadcrumb" data-control-id={control.id} data-control-type="breadcrumb">
      <ol style={{ ...navStyle, listStyle: 'none', margin: 0, padding: 0 }}>
        {items.map((item, i) => (
          <React.Fragment key={i}>
            {i > 0 && (
              <li style={separatorStyle} aria-hidden="true">
                <ChevronRightIcon width={14} height={14} />
              </li>
            )}
            <li>
              {item.href && mode === 'view' ? (
                <a href={item.href} style={itemStyle(i)} aria-current={i === activeIndex ? 'page' : undefined}>
                  {item.label}
                </a>
              ) : (
                <span
                  style={itemStyle(i)}
                  aria-current={i === activeIndex ? 'page' : undefined}
                  onClick={() => mode !== 'view' && onChange && i < activeIndex ? onChange(i) : undefined}
                  role={mode !== 'view' && onChange && i < activeIndex ? 'button' : undefined}
                  tabIndex={mode !== 'view' && onChange && i < activeIndex ? 0 : undefined}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' && mode !== 'view' && onChange && i < activeIndex) onChange(i);
                  }}
                >
                  {item.label}
                </span>
              )}
            </li>
          </React.Fragment>
        ))}
      </ol>
    </nav>
  );
});
